import React, { useEffect, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import {
  MessageCircle,
  Mail,
  CircleCheck,
  Clock,
  TrendingDown,
  UsersRound,
  Gauge,
  ArrowUp,
  ArrowDown,
  ArrowLeftRight,
  RefreshCw,
  BadgeCheck,
  Calendar,
  ChevronDown,
  LayoutGrid,
} from "lucide-react";
import { toast } from "sonner";
import { FaWhatsapp } from "react-icons/fa";
import { RiMessage2Line } from "react-icons/ri";
import useCrmApi from "../../../components/hook/crm-req";
import useRequest from "../../../components/hook/use-req";
import { TeamDetails } from "../../../components/crm/customer/types";
import { DownloadIcon } from "../../../assets/icon";
import { useTheme } from "../../../context/theme-context";

// ─────────────────────────────────────────────────────────────
// Palette (validated: node scripts/validate_palette.js "#4f46e5,#65a30d,#16a34a" --mode light → PASS)
// ─────────────────────────────────────────────────────────────

const SERIES_COLOR = {
  incoming: "#4f46e5", // indigo
  open: "#65a30d", // lime
  closed: "#16a34a", // green
};
const INK_LIGHT = { primary: "#0b0b0b", secondary: "#52514e", muted: "#898781", grid: "#e1e0d9" };
// ECharts renders to canvas, not the DOM, so Tailwind's `dark:` classes can't
// reach it — axis/grid/tooltip colors have to be picked in JS from the same
// useTheme() the rest of the page uses instead.
const INK_DARK = { primary: "#e5e7eb", secondary: "#c7c7c7", muted: "#8b8b8b", grid: "#374151" };
const getInk = (isDark: boolean) => (isDark ? INK_DARK : INK_LIGHT);

type Period = "daily" | "weekly" | "monthly";
type AnalyticsView = "realtime" | "custom";
type ChannelFilter = "whatsapp" | "sms" | "email" | "rcs" | "all";
type CustomDateRange = { start: string; end: string };

// Picking a period sets a matching default window (daily = today, weekly =
// last 7 days, monthly = last 30 days) rather than leaving whatever custom
// range happened to be active before.
const getDefaultRangeForPeriod = (period: Period): CustomDateRange => {
  const toISODate = (d: Date) => d.toISOString().slice(0, 10);
  const end = new Date();
  const start = new Date(end);
  if (period === "weekly") start.setDate(start.getDate() - 6);
  else if (period === "monthly") start.setDate(start.getDate() - 29);
  return { start: toISODate(start), end: toISODate(end) };
};

// ─────────────────────────────────────────────────────────────
// API types — see conversation-analytics-archive-api.md
// ─────────────────────────────────────────────────────────────

interface SummaryMetric {
  value: number;
  previous_value: number;
  delta: number;
}

interface AnalyticsSummary {
  total_incoming: SummaryMetric;
  engagements: SummaryMetric;
  open: SummaryMetric;
  closed: SummaryMetric;
  pending: SummaryMetric;
  missed: SummaryMetric;
  potential_leads: SummaryMetric;
  qualified_leads: SummaryMetric;
  avg_intent_score: SummaryMetric;
}

interface ConversationTrendPoint {
  date: string;
  label: string;
  incoming: number;
  open: number;
  closed: number;
}

interface LeadTrendPoint {
  date: string;
  label: string;
  leads_converted: number;
}

interface ConversationTagPoint {
  tag: string;
  count: number;
}

interface PeakHourPoint {
  hour: number;
  label: string;
  incoming: number;
}

interface AnalyticsResponse {
  period: Period;
  start_date: string;
  end_date: string;
  summary: AnalyticsSummary;
  conversation_trends: ConversationTrendPoint[];
  lead_conversion_trends: LeadTrendPoint[];
  conversation_tags: ConversationTagPoint[];
  peak_conversation_hours: PeakHourPoint[];
}

// GET /conversations/analytics/realtime
interface RealtimeMetric {
  value: number | null;
  unit: string | null;
  label: string | null;
  available: boolean;
}

interface RealtimeResponse {
  generated_at: string;
  lookback_minutes: number;
  metrics: {
    total_incoming: RealtimeMetric;
    missed: RealtimeMetric;
    transferred: RealtimeMetric;
    longest_wait_time: RealtimeMetric;
    shortest_wait_time: RealtimeMetric;
    avg_wait_time: RealtimeMetric;
    active_conversations: RealtimeMetric;
    conversation_load: RealtimeMetric;
  };
}

// ─────────────────────────────────────────────────────────────
// Small UI atoms
// ─────────────────────────────────────────────────────────────

interface StatTileData {
  id: string;
  label: string;
  value: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  delta?: { text: string; direction: "up" | "down"; tone: "good" | "bad" };
  caption?: string;
  onDownload?: () => void;
  downloading?: boolean;
  // Raw numeric count backing `value` — used to disable the download button
  // when there's nothing to download (value is already a formatted string).
  rawValue?: number;
}

const StatTile: React.FC<StatTileData> = ({ label, value, icon, iconBg, iconColor, delta, caption, onDownload, downloading, rawValue }) => {
  const noData = rawValue === 0;
  return (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 flex flex-col gap-3 min-w-0">
    <div className="flex items-start justify-between gap-2 min-w-0">
      <div className="flex items-start gap-2 min-w-0">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${iconBg} ${iconColor}`}>{icon}</span>
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{label}</span>
      </div>
      {onDownload && (
        <button
          onClick={onDownload}
          disabled={downloading || noData}
          title={noData ? `Nothing to download` : `Download ${label}`}
          className="shrink-0 text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer p-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {downloading ? (
            <div className="w-3.5 h-3.5 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
          ) : (
            <DownloadIcon/>
          )}
        </button>
      )}
    </div>
    <div>
      <span className={delta ? "text-2xl font-semibold text-indigo-600 dark:text-indigo-400 underline decoration-2 underline-offset-2" : "text-2xl font-semibold text-orange-500 dark:text-orange-400"}>
        {value}
      </span>
    </div>
    {delta && (
      <p className={`flex items-center gap-1 text-xs font-medium ${delta.tone === "good" ? "text-green-600" : "text-red-500"}`}>
        {delta.direction === "up" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
        {delta.text}
      </p>
    )}
    {caption && <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">{caption}</p>}
  </div>
  );
};

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const PeriodDropdown: React.FC<{ value: Period; onChange: (p: Period) => void }> = ({ value, onChange }) => (
  <div className="relative flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg pl-3 pr-7 py-2 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
    <Clock size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as Period)}
      className="appearance-none bg-transparent outline-none border-none cursor-pointer text-sm pr-1"
    >
      {PERIOD_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <ChevronDown size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500 absolute right-2.5 pointer-events-none" />
  </div>
);

const ViewToggle: React.FC<{ value: AnalyticsView; onChange: (v: AnalyticsView) => void }> = ({ value, onChange }) => (
  <div className="flex items-center gap-2 shrink-0">
    {(["realtime", "custom"] as AnalyticsView[]).map((v) => (
      <button
        key={v}
        onClick={() => onChange(v)}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
          value === v ? "bg-gray-900 dark:bg-gray-700 text-white" : "bg-indigo-50 dark:bg-indigo-500/15 text-gray-500 dark:text-gray-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/25"
        }`}
      >
        {v === "realtime" ? "Real-time" : "Custom Range"}
      </button>
    ))}
  </div>
);

// Manual refresh for the real-time view — re-fetches /conversations/analytics/realtime.
const RefreshActivity: React.FC<{ onClick: () => void; loading?: boolean }> = ({ onClick, loading }) => {
  const [spinning, setSpinning] = useState(false);
  const isSpinning = spinning || loading;

  const handleClick = () => {
    if (isSpinning) return;
    setSpinning(true);
    onClick();
    setTimeout(() => setSpinning(false), 700);
  };

  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg px-3.5 py-2 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors whitespace-nowrap"
    >
      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
      Refresh Activity
      <RefreshCw size={14} className={`text-[#7B7B7B] font-semibold dark:text-gray-500 ${isSpinning ? "animate-spin" : ""}`} />
    </button>
  );
};

const CHANNEL_OPTIONS: { value: ChannelFilter; label: string; icon: React.ReactNode }[] = [
  { value: "whatsapp", label: "WhatsApp", icon: <FaWhatsapp size={14} className="text-[#25D366]" /> },
  { value: "email", label: "Email", icon: <Mail size={14} className="text-amber-500" /> },
  { value: "rcs", label: "RCS", icon: <RiMessage2Line size={14} className="text-[#5E35B1]" /> },
  { value: "all", label: "All Channels", icon: <LayoutGrid size={14} className="text-gray-500 dark:text-gray-400" /> },
];

const ChannelDropdown: React.FC<{ value: ChannelFilter; onChange: (v: ChannelFilter) => void }> = ({ value, onChange }) => {
  const current = CHANNEL_OPTIONS?.find((c) => c.value === value) ?? CHANNEL_OPTIONS[0];
  return (
    <div className="relative flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg pl-3 pr-7 py-2 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
      {current.icon}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as ChannelFilter)}
        className="appearance-none bg-transparent outline-none border-none cursor-pointer text-sm pr-1"
      >
        {CHANNEL_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500 absolute right-2.5 pointer-events-none" />
    </div>
  );
};

const AgentDropdown: React.FC<{ value: string; agents: TeamDetails[]; onChange: (v: string) => void }> = ({
  value,
  agents,
  onChange,
}) => (
  <div className="relative flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg pl-3 pr-7 py-2 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
    <UsersRound size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none bg-transparent outline-none border-none cursor-pointer text-sm pr-1"
    >
      <option value="">All Agents</option>
      {agents.map((a) => (
        <option key={a.id} value={a.id}>
          {`${a.first_name || ""} ${a.last_name || ""}`.trim() || a.email}
        </option>
      ))}
    </select>
    <ChevronDown size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500 absolute right-2.5 pointer-events-none" />
  </div>
);

// Custom date-range picker — a small popover with native date inputs, same
// pattern as the platform dashboard's range picker. `fallbackLabel` shows the
// window the backend is actually using (from the analytics response) until
// the user explicitly applies their own range.
const DateRangeFilter: React.FC<{
  range: CustomDateRange | null;
  fallbackLabel?: string | null;
  onApply: (range: CustomDateRange) => void;
  onClear: () => void;
}> = ({ range, fallbackLabel, onApply, onClear }) => {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(range?.start ?? "");
  const [end, setEnd] = useState(range?.end ?? "");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setStart(range?.start ?? "");
    setEnd(range?.end ?? "");
  }, [range]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const label = range
    ? `${new Date(range.start).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })} - ${new Date(
        range.end,
      ).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })}`
    : fallbackLabel || "Custom Range";

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg px-3.5 py-2 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors whitespace-nowrap"
      >
        <Calendar size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
        {label}
        <ChevronDown size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-lg shadow-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 z-50 p-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md px-3 py-1.5 text-sm"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
                className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Clear
              </button>
              <button
                onClick={() => {
                  if (start && end) {
                    onApply({ start, end });
                    setOpen(false);
                  }
                }}
                disabled={!start || !end}
                className="px-3 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-xs font-medium rounded-md disabled:opacity-40"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ChartLegend: React.FC<{ items: { label: string; color: string }[] }> = ({ items }) => (
  <div className="flex items-center gap-4 pb-3 border-b border-gray-100 dark:border-gray-800 flex-wrap">
    {items.map((it) => (
      <span key={it.label} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: it.color }} />
        {it.label}
      </span>
    ))}
  </div>
);

const ChartCard: React.FC<{
  title: string;
  subtitle: string;
  rightSlot?: React.ReactNode;
  legend?: { label: string; color: string }[];
  children: React.ReactNode;
}> = ({ title, subtitle, rightSlot, legend, children }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5 flex flex-col gap-4 min-w-0">
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h3 className="text-base font-semibold text-gray-500 dark:text-gray-400">{title}</h3>
        <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">{subtitle}</p>
      </div>
      {rightSlot}
    </div>
    {legend && <ChartLegend items={legend} />}
    <div className="w-full h-64 sm:h-72">{children}</div>
  </div>
);

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <div className="w-full h-full flex items-center justify-center text-center text-sm text-[#7B7B7B] font-semibold dark:text-gray-500 px-6">{text}</div>
);

// ─────────────────────────────────────────────────────────────
// Charts
// ─────────────────────────────────────────────────────────────

const getBaseGridAxis = (ink: typeof INK_LIGHT) => ({
  axisLine: { lineStyle: { color: ink.grid } },
  axisTick: { show: false },
  axisLabel: { color: ink.muted, fontSize: 11 },
});

// Shared dark-aware tooltip styling — ECharts' default tooltip is a white
// box, which reads fine on the light page but disappears against a dark one.
const getTooltipTheme = (isDark: boolean) =>
  isDark ? { backgroundColor: "#1f2937", borderColor: "#374151", textStyle: { color: "#e5e7eb" } } : {};

const TrendsChart: React.FC<{ data: ConversationTrendPoint[]; isDark: boolean }> = ({ data, isDark }) => {
  const ink = getInk(isDark);
  const option = {
    grid: { left: 36, right: 12, top: 16, bottom: 28 },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, textStyle: { fontSize: 12 }, ...getTooltipTheme(isDark) },
    legend: { show: false },
    xAxis: { type: "category", data: data.map((d) => d.label), ...getBaseGridAxis(ink) },
    yAxis: { type: "value", splitLine: { lineStyle: { color: ink.grid, type: "dashed" } }, axisLabel: { color: ink.muted, fontSize: 11 } },
    series: [
      { name: "Incoming", type: "bar", data: data.map((d) => d.incoming), barWidth: 10, barGap: "20%", itemStyle: { color: SERIES_COLOR.incoming, borderRadius: [4, 4, 0, 0] } },
      { name: "Open", type: "bar", data: data.map((d) => d.open), barWidth: 10, itemStyle: { color: SERIES_COLOR.open, borderRadius: [4, 4, 0, 0] } },
      { name: "Closed", type: "bar", data: data.map((d) => d.closed), barWidth: 10, itemStyle: { color: SERIES_COLOR.closed, borderRadius: [4, 4, 0, 0] } },
    ],
  };

  return <ReactECharts option={option} style={{ width: "100%", height: "100%" }} notMerge lazyUpdate />;
};

const LeadsChart: React.FC<{ data: LeadTrendPoint[]; isDark: boolean }> = ({ data, isDark }) => {
  const ink = getInk(isDark);
  const option = {
    grid: { left: 36, right: 12, top: 16, bottom: 20 },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, textStyle: { fontSize: 12 }, ...getTooltipTheme(isDark) },
    xAxis: { type: "category", data: data.map((d) => d.label), ...getBaseGridAxis(ink) },
    yAxis: { type: "value", splitLine: { lineStyle: { color: ink.grid, type: "dashed" } }, axisLabel: { color: ink.muted, fontSize: 11 } },
    series: [
      {
        name: "Leads",
        type: "bar",
        data: data.map((d) => d.leads_converted),
        barWidth: 14,
        itemStyle: { color: SERIES_COLOR.incoming, borderRadius: [4, 4, 0, 0] },
      },
    ],
  };

  return <ReactECharts option={option} style={{ width: "100%", height: "100%" }} notMerge lazyUpdate />;
};

const PeakHoursChart: React.FC<{ data: PeakHourPoint[]; isDark: boolean }> = ({ data, isDark }) => {
  if (data.length === 0) return <EmptyState text="No peak-hour activity in this period." />;

  const ink = getInk(isDark);
  const values = data.map((d) => d.incoming);
  const peakIndex = values.indexOf(Math.max(...values));

  const option = {
    grid: { left: 36, right: 12, top: 40, bottom: 20 },
    tooltip: { trigger: "axis", textStyle: { fontSize: 12 }, ...getTooltipTheme(isDark) },
    xAxis: { type: "category", data: data.map((d) => d.label), boundaryGap: false, ...getBaseGridAxis(ink) },
    yAxis: { type: "value", splitLine: { lineStyle: { color: ink.grid, type: "dashed" } }, axisLabel: { color: ink.muted, fontSize: 11 } },
    series: [
      {
        name: "Incoming",
        type: "line",
        data: values,
        showSymbol: false,
        lineStyle: { width: 2, color: SERIES_COLOR.incoming },
        areaStyle: { color: SERIES_COLOR.incoming, opacity: 0.1 },
        markLine: {
          symbol: "none",
          silent: true,
          data: [{ xAxis: peakIndex }],
          lineStyle: { type: "dashed", color: ink.grid, width: 1 },
          label: { show: false },
        },
        markPoint: {
          data: [{ coord: [peakIndex, values[peakIndex]], value: values[peakIndex] }],
          symbol: "circle",
          symbolSize: 8,
          itemStyle: { color: SERIES_COLOR.incoming, borderColor: "#fff", borderWidth: 2 },
          label: {
            show: true,
            formatter: `${values[peakIndex]} messages`,
            position: "top",
            distance: 14,
            backgroundColor: "#1f2937",
            color: "#fff",
            padding: [6, 10],
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 500,
          },
        },
      },
    ],
  };

  return <ReactECharts option={option} style={{ width: "100%", height: "100%" }} notMerge lazyUpdate />;
};

const TagUsageList: React.FC<{ data: ConversationTagPoint[] }> = ({ data }) => {
  if (data.length === 0) return <EmptyState text="No tagged conversations in this period." />;

  const total = data.reduce((sum, t) => sum + t.count, 0);

  return (
    <div className="flex flex-col gap-5">
      {data.map((t) => {
        const pct = total > 0 ? Math.round((t.count / total) * 100) : 0;
        return (
          <div key={t.tag}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{t.tag}</span>
            </div>
            <div className="h-2 w-full bg-indigo-100 dark:bg-indigo-500/20 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${pct}%` }} />
            </div>
            <span className="block text-right text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Stat tile construction from the real summary payload
// ─────────────────────────────────────────────────────────────

const deltaFor = (metric: SummaryMetric, unit: string, goodWhenUp: boolean): StatTileData["delta"] => {
  const direction: "up" | "down" = metric.delta >= 0 ? "up" : "down";
  const tone: "good" | "bad" = (metric.delta >= 0) === goodWhenUp ? "good" : "bad";
  return { text: `${metric.delta >= 0 ? "+" : ""}${metric.delta} vs ${unit}`, direction, tone };
};

// GET /conversations/analytics/export?metric=...&start_date&end_date&channel&format
// — one row per contributing conversation, full transcript included. Only
// these 3 tiles have a download action for now.
const EXPORTABLE_METRICS: Record<string, string> = {
  incoming: "total_incoming",
  leads: "potential_leads",
  "qualified-leads": "qualified_leads",
};

const buildCustomStatTiles = (
  summary: AnalyticsSummary,
  onDownload: (tileId: string, label: string) => void,
): StatTileData[] => [
  {
    id: "incoming",
    label: "Total Conversations",
    value: String(summary?.engagements.value),
    icon: <MessageCircle size={16} />,
    iconBg: "bg-indigo-50",
    iconColor: "text-indigo-600",
    delta: deltaFor(summary.engagements, "Last Period", true),
    onDownload: () => onDownload("incoming", "Total Conversations"),
    rawValue: summary.engagements.value,
  },
  {
    id: "open",
    label: "Open Conversations",
    value: String(summary.open.value),
    icon: <Mail size={16} />,
    iconBg: "bg-green-50",
    iconColor: "text-green-600",
    delta: deltaFor(summary.open, "Last Period", true),
  },
  {
    id: "closed",
    label: "Closed Conversations",
    value: String(summary.closed.value),
    icon: <CircleCheck size={16} />,
    iconBg: "bg-green-50",
    iconColor: "text-green-600",
    delta: deltaFor(summary.closed, "Last Period", true),
  },
  {
    id: "pending",
    label: "Pending Conversations",
    value: String(summary.pending.value),
    icon: <Clock size={16} />,
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
    delta: deltaFor(summary.pending, "Last Period", true),
  },
  {
    id: "missed",
    label: "Missed Conversations",
    value: String(summary.missed.value),
    icon: <TrendingDown size={16} />,
    iconBg: "bg-red-50",
    iconColor: "text-red-600",
    delta: deltaFor(summary.missed, "Last Period", false),
  },
  {
    id: "leads",
    label: "Potential Leads",
    value: String(summary.potential_leads.value),
    icon: <UsersRound size={16} />,
    iconBg: "bg-indigo-50",
    iconColor: "text-indigo-600",
    delta: deltaFor(summary.potential_leads, "Last Period", true),
    onDownload: () => onDownload("leads", "Potential Leads"),
    rawValue: summary.potential_leads.value,
  },
  {
    id: "qualified-leads",
    label: "Qualified Leads",
    value: String(summary.qualified_leads.value),
    icon: <BadgeCheck size={16} />,
    iconBg: "bg-indigo-50",
    iconColor: "text-indigo-600",
    delta: deltaFor(summary.qualified_leads, "Last Period", true),
    onDownload: () => onDownload("qualified-leads", "Qualified Leads"),
    rawValue: summary.qualified_leads.value,
  },
  {
    id: "intent",
    label: "Avg. Intent Score",
    value: `${summary.avg_intent_score.value}/ 100`,
    icon: <Gauge size={16} />,
    iconBg: "bg-indigo-50",
    iconColor: "text-indigo-600",
    caption: "All Leads average",
  },
];

// GET /conversations/analytics/realtime has no previous_value/delta on any
// metric, so real-time tiles never show a trend arrow — just the plain value
// (or the "Not available" caption when a wait-time metric has no data yet).
const countTile = (id: string, label: string, metric: RealtimeMetric, icon: React.ReactNode, iconBg: string, iconColor: string): StatTileData => ({
  id,
  label,
  value: metric.available && metric.value !== null ? String(metric.value) : "—",
  icon,
  iconBg,
  iconColor,
  caption: metric.available ? undefined : "Not available yet",
});

const waitTile = (id: string, label: string, metric: RealtimeMetric): StatTileData => ({
  id,
  label,
  value: metric.available && metric.label ? metric.label : "—",
  icon: <Clock size={16} />,
  iconBg: "bg-indigo-50",
  iconColor: "text-indigo-600",
  caption: metric.available ? "All agents average" : "No wait data yet",
});

const buildRealtimeStatTiles = (metrics: RealtimeResponse["metrics"]): StatTileData[] => [
  countTile("incoming", "Total Incoming", metrics.total_incoming, <MessageCircle size={16} />, "bg-indigo-50", "text-indigo-600"),
  countTile("missed", "Missed", metrics.missed, <TrendingDown size={16} />, "bg-red-50", "text-red-600"),
  countTile("transferred", "Transferred", metrics.transferred, <ArrowLeftRight size={16} />, "bg-indigo-50", "text-indigo-600"),
  waitTile("longest-wait", "Longest Wait Time", metrics.longest_wait_time),
  waitTile("shortest-wait", "Shortest Wait Time", metrics.shortest_wait_time),
  waitTile("avg-wait", "Avg. Wait Time", metrics.avg_wait_time),
  countTile("active", "Active Conversations", metrics.active_conversations, <MessageCircle size={16} />, "bg-indigo-50", "text-indigo-600"),
  countTile("load", "Conversation Load", metrics.conversation_load, <Gauge size={16} />, "bg-indigo-50", "text-indigo-600"),
];

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────

const MarketingAnalytics: React.FC = () => {
  const { isDark } = useTheme();
  const token = localStorage.getItem("authToken");
  const [view, setView] = useState<AnalyticsView>("custom");
  const [period, setPeriod] = useState<Period>("daily");
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [realtime, setRealtime] = useState<RealtimeResponse | null>(null);
  const [realtimeLoading, setRealtimeLoading] = useState(true);
  const [realtimeError, setRealtimeError] = useState(false);

  // Channel / agent / custom-range filters — forwarded as query params to the
  // same real endpoints. Agents come from the real team roster, not a mock list.
  const [channel, setChannel] = useState<ChannelFilter>("whatsapp");
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange | null>(null);
  const [agents, setAgents] = useState<TeamDetails[]>([]);
  const [downloadingTile, setDownloadingTile] = useState<string | null>(null);

  const handlePeriodChange = (newPeriod: Period) => {
    setPeriod(newPeriod);
    setCustomDateRange(getDefaultRangeForPeriod(newPeriod));
  };

  const { makeRequest: getAnalytics } = useCrmApi(`/conversations/analytics`, "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: getRealtimeAnalytics } = useCrmApi(`/conversations/analytics/realtime`, "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: getTeamList } = useRequest(`/profile/users`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const [response] = await getTeamList();
        if (Array.isArray(response)) setAgents(response);
        else if (Array.isArray(response?.users)) setAgents(response.users);
        else if (Array.isArray(response?.results)) setAgents(response.results);
      } catch {
        setAgents([]);
      }
    };
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError(false);
      try {
        const params: Record<string, any> = { period };
        if (channel !== "all") params.channel = channel;
        if (selectedAgentId) params.agent_id = selectedAgentId;
        if (customDateRange) {
          params.start_date = customDateRange.start;
          params.end_date = customDateRange.end;
        }
        const [response, status] = await getAnalytics(undefined, params);
        if (status < 200 || status >= 300 || !response?.summary) {
          setError(true);
          setAnalytics(null);
        } else {
          setAnalytics(response);
        }
      } catch (err) {
        console.error("Error fetching conversation analytics:", err);
        setError(true);
        setAnalytics(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, channel, selectedAgentId, customDateRange]);

  const fetchRealtimeAnalytics = async () => {
    setRealtimeLoading(true);
    setRealtimeError(false);
    try {
      const params: Record<string, any> = { lookback_minutes: 60 };
      if (channel !== "all") params.channel = channel;
      if (selectedAgentId) params.agent_id = selectedAgentId;
      const [response, status] = await getRealtimeAnalytics(undefined, params);
      if (status < 200 || status >= 300 || !response?.metrics) {
        setRealtimeError(true);
        setRealtime(null);
      } else {
        setRealtime(response);
      }
    } catch (err) {
      console.error("Error fetching real-time queue overview:", err);
      setRealtimeError(true);
      setRealtime(null);
    } finally {
      setRealtimeLoading(false);
    }
  };

  // GET /conversations/analytics/export — returns an actual xlsx/csv file
  // (Content-Disposition attachment), not JSON, so this bypasses useCrmApi
  // (which only handles json/text responses) and fetches directly for the blob.
  const downloadAnalyticsMetric = async (tileId: string, label: string) => {
    if (downloadingTile) return;
    const metric = EXPORTABLE_METRICS[tileId];
    if (!metric) return;

    setDownloadingTile(tileId);
    try {
      const params = new URLSearchParams();
      params.set("metric", metric);
      if (customDateRange) {
        params.set("start_date", customDateRange.start);
        params.set("end_date", customDateRange.end);
      } else if (analytics) {
        params.set("start_date", analytics.start_date);
        params.set("end_date", analytics.end_date);
      }
      if (channel !== "all") params.set("channel", channel);
      params.set("format", "xlsx");

      const baseUrl = process.env.REACT_APP_BACKEND_URL || "https://api.crm.dalmlabs.com/api/v1";
      const res = await fetch(`${baseUrl}/conversations/analytics/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed with status ${res.status}`);

      const blob = await res.blob();
      const disposition = res.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || `${metric}_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${label} exported`);
    } catch (err) {
      console.error("Error exporting analytics metric:", err);
      toast.error(`Failed to export ${label}`);
    } finally {
      setDownloadingTile(null);
    }
  };

  useEffect(() => {
    if (view === "realtime") fetchRealtimeAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, channel, selectedAgentId]);

  const isRealtime = view === "realtime";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Conversation Analytics</h1>
        <ViewToggle value={view} onChange={setView} />
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {isRealtime ? "Real-time Queue Overview" : "Conversation Performance"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isRealtime
              ? "Monitor conversations, agent workload, and response performance in real-time"
              : "Monitor conversations, agent workload, and response performance."}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isRealtime ? (
            <RefreshActivity onClick={fetchRealtimeAnalytics} loading={realtimeLoading} />
          ) : (
            <>
              <PeriodDropdown value={period} onChange={handlePeriodChange} />
              <DateRangeFilter
                range={customDateRange}
                fallbackLabel={analytics ? `${analytics.start_date} - ${analytics.end_date}` : null}
                onApply={setCustomDateRange}
                onClear={() => setCustomDateRange(null)}
              />
            </>
          )}
          <AgentDropdown value={selectedAgentId} agents={agents} onChange={setSelectedAgentId} />
          <ChannelDropdown value={channel} onChange={setChannel} />
        </div>
      </div>

      {isRealtime && realtime && (
        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 -mt-4">
          Updated {new Date(realtime.generated_at).toLocaleTimeString()} · last {realtime.lookback_minutes}m
        </p>
      )}

      {!isRealtime && loading && <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading analytics…</p>}
      {!isRealtime && error && <p className="text-sm text-red-500">Couldn't load analytics. Try again shortly.</p>}
      {isRealtime && realtimeLoading && <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading real-time metrics…</p>}
      {isRealtime && realtimeError && <p className="text-sm text-red-500">Couldn't load real-time metrics. Try refreshing.</p>}

      {/* Capped at 4 per row for now regardless of viewport width — was
          scaling up to 6/7 columns on xl screens before. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {(isRealtime
          ? realtime
            ? buildRealtimeStatTiles(realtime.metrics)
            : []
          : analytics
            ? buildCustomStatTiles(analytics.summary, downloadAnalyticsMetric)
            : []
        ).map((tile) => (
          <StatTile key={tile.id} {...tile} downloading={downloadingTile === tile.id} />
        ))}
      </div>

      {!isRealtime && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard
              title="Conversation Analytics"
              subtitle="Conversation Trends"
              legend={[
                { label: "Incoming", color: SERIES_COLOR.incoming },
                { label: "Open", color: SERIES_COLOR.open },
                { label: "Closed", color: SERIES_COLOR.closed },
              ]}
            >
              {analytics ? <TrendsChart data={analytics.conversation_trends} isDark={isDark} /> : <EmptyState text={loading ? "Loading…" : "No data available."} />}
            </ChartCard>

            <ChartCard
              title="Leads"
              subtitle="Lead Conversion Trends"
              legend={[{ label: "Leads Converted", color: SERIES_COLOR.incoming }]}
            >
              {analytics ? <LeadsChart data={analytics.lead_conversion_trends} isDark={isDark} /> : <EmptyState text={loading ? "Loading…" : "No data available."} />}
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                  <h3 className="text-base font-semibold text-gray-500 dark:text-gray-400">Conversation Tags</h3>
                  <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Tag Usage</p>
                </div>
                {analytics && (
                  <div className="text-right text-sm text-gray-500 dark:text-gray-400 space-y-0.5">
                    <p>
                      Total Tags: <span className="font-semibold text-gray-800 dark:text-gray-200">{analytics.conversation_tags.length}</span>
                    </p>
                    <p>
                      Total Usage:{" "}
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {analytics.conversation_tags.reduce((sum, t) => sum + t.count, 0)} times
                      </span>
                    </p>
                  </div>
                )}
              </div>
              <div className="max-h-64 overflow-y-auto pr-1">
                {analytics ? <TagUsageList data={analytics.conversation_tags} /> : <EmptyState text={loading ? "Loading…" : "No data available."} />}
              </div>
            </div>

            <ChartCard title="Peak Conversation Hours" subtitle="Discover your busiest conversation hours.">
              {analytics ? <PeakHoursChart data={analytics.peak_conversation_hours} isDark={isDark} /> : <EmptyState text={loading ? "Loading…" : "No data available."} />}
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
};

export default MarketingAnalytics;
